import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Play, X } from 'lucide-react';
import { useT } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';
import { useAuth } from '../lib/auth';
import { resolveCta } from '../lib/ctaActions';

// ─────────────────────────────────────────────────────────────────────────
// ModernHero — the «Moderno» theme hero (s139, approved by Anthony: option F
// "Cinematic"). Full-bleed background photo like a cover, copy bottom-left
// aligned with the navbar logo, the slide's photo/video as a thumbnail (video
// opens full-screen on click, never autoplays), slides shown as chapters.
// Same slide fields as HeroSection (title/subtitle/description/3 CTAs with
// Action/photo|video/background/carousel). Colours come only from theme tokens.
// Keeps data-testid="hero-section" for the transparent navbar.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
const FILL = 'cubic-bezier(0.77, 0, 0.175, 1)';

const CSS = `
.mh-btn{position:relative;isolation:isolate;overflow:hidden;display:inline-flex;align-items:center;justify-content:center;gap:.55rem;font-weight:600;font-size:.875rem;letter-spacing:.02em;padding:.95rem 1.6rem;border-radius:2px;white-space:nowrap;transition:transform 160ms ease-out,background-color 200ms ease,color 320ms ${FILL},border-color 320ms ${FILL},opacity 200ms ease}
.mh-btn:active{transform:scale(.96)}
.mh-btn:focus-visible{outline:2px solid #fff;outline-offset:3px}
.mh-btn--primary{background:var(--color-accent,#ff9c1b);color:var(--accent-ink,#0f172a)}
.mh-btn--outline{border:1.5px solid rgba(255,255,255,.7);color:#fff}
.mh-btn--link{padding-left:.25rem;padding-right:.25rem;color:#fff;text-decoration:underline;text-underline-offset:.35em;text-decoration-thickness:1.5px;text-decoration-color:var(--color-accent,#ff9c1b)}
/* "Relleno" (s140, Anthony): primary fills white from below, outline fills
   with the accent from the left, the link gets an accent marker behind it. */
.mh-btn::before{content:"";position:absolute;z-index:-1;transition:transform 380ms ${FILL},opacity 200ms ease}
.mh-btn--primary::before{inset:-2px;background:#fff;transform:translateY(101%)}
.mh-btn--outline::before{inset:-2px;background:var(--color-accent,#ff9c1b);transform:scaleX(0);transform-origin:left center}
.mh-btn--link::before{left:0;right:0;bottom:.55rem;height:.62em;background:color-mix(in srgb,var(--color-accent,#ff9c1b) 45%,transparent);transform:scaleX(0);transform-origin:left center}
.mh-btn--primary:is(:focus-visible,:active){color:var(--color-primary,#1a2332)}
.mh-btn--outline:is(:focus-visible,:active){color:var(--accent-ink,#0f172a);border-color:var(--color-accent,#ff9c1b)}
.mh-btn:is(:focus-visible,:active)::before{transform:none}
.mh-btn--primary:is(:focus-visible,:active) .mh-arrow{transform:rotate(-45deg)}
@media (hover:hover) and (pointer:fine){
  .mh-btn--primary:hover{color:var(--color-primary,#1a2332)}
  .mh-btn--outline:hover{color:var(--accent-ink,#0f172a);border-color:var(--color-accent,#ff9c1b)}
  .mh-btn:hover::before{transform:none}
  .mh-btn--primary:hover .mh-arrow{transform:rotate(-45deg)}
  .mh-btn--link:hover{text-decoration-color:#fff}
}
@media (prefers-reduced-motion:reduce){
  .mh-btn::before{transform:none!important;opacity:0;transition:opacity 220ms ease}
  .mh-btn:is(:hover,:focus-visible,:active)::before{opacity:1}
  .mh-btn:active{transform:none}
  .mh-arrow{transform:none!important}
}
.mh-arrow{transition:transform 320ms ${FILL}}
.mh-rich p{margin:0}
.mh-rich em{font-style:italic}
.mh-title em,.mh-title i{color:var(--color-accent,#ff9c1b)}
.mh-eyebrow{display:inline-flex;align-items:center;gap:.75rem;font-size:.72rem;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--color-accent,#ff9c1b)}
.mh-eyebrow::before{content:"";width:2rem;height:2px;background:var(--color-accent,#ff9c1b)}
.mh-enter>*{opacity:0;transform:translateY(10px);animation:mhIn 520ms ${EASE} forwards}
.mh-enter>*:nth-child(2){animation-delay:60ms}.mh-enter>*:nth-child(3){animation-delay:120ms}.mh-enter>*:nth-child(4){animation-delay:180ms}.mh-enter>*:nth-child(5){animation-delay:240ms}
@keyframes mhIn{to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.mh-enter>*{animation:mhFade 300ms ease forwards;transform:none}@keyframes mhFade{to{opacity:1}}}
`;

function useSlideCtas(slide) {
  const tt = useT();
  const { user } = useAuth();
  const loggedIn = !!user;
  return [
    { text: slide.button_text,   url: tt(slide.button_url || slide.button_link), target: slide.window_open === 'new' ? '_blank' : '_self', action: slide.button_action },
    { text: slide.button_2_text, url: tt(slide.button_2_url), target: slide.button_2_window_open === 'new' ? '_blank' : '_self', action: slide.button_2_action },
    { text: slide.button_3_text, url: tt(slide.button_3_url), target: slide.button_3_window_open === 'new' ? '_blank' : '_self', action: slide.button_3_action },
  ]
    .filter(b => tt(b.text))
    .map(b => { const r = resolveCta(b, { loggedIn }); return r.visible ? { ...b, label: tt(b.text), href: r.href, target: r.target } : null; })
    .filter(Boolean);
}

// Primary = solid accent · 2nd = outline · 3rd = text link (clear hierarchy).
function Ctas({ slide, stack = false, center = false }) {
  const ctas = useSlideCtas(slide);
  if (!ctas.length) return null;
  const kinds = ['primary', 'outline', 'link'];
  return (
    <div className={`flex flex-col sm:flex-row sm:flex-wrap gap-3 ${center ? 'sm:justify-center' : ''} ${stack ? '' : 'sm:items-center'}`} data-testid="hero-cta-row">
      {ctas.map((b, i) => (
        <a key={i} href={b.href || '#'} target={b.target} rel="noopener noreferrer" className={`mh-btn mh-btn--${kinds[i]}`} data-testid={`hero-cta-btn-${i}`}>
          {b.label}{i === 0 && <ArrowRight className="w-4 h-4 mh-arrow" />}
        </a>
      ))}
    </div>
  );
}

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?\s]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  if (url.startsWith('<iframe')) { const m = url.match(/src=["']([^"']+)["']/); if (m) return m[1]; }
  return url;
}

function Media({ slide, className = '', style }) {
  if (slide.slide_type === 'video' && slide.video_embed) {
    return (
      <div className={`aspect-video ${className}`} style={style}>
        <iframe src={embedUrl(slide.video_embed)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen frameBorder="0" title="Hero video" />
      </div>
    );
  }
  if (slide.slide_type === 'photo' && slide.photo) {
    return <img src={slide.photo} alt="" className={`w-full h-full object-cover ${className}`} style={style} />;
  }
  return null;
}
const hasMedia = (s) => (s.slide_type === 'video' && s.video_embed) || (s.slide_type === 'photo' && s.photo);

function Copy({ slide, center = false, titleSize, maxW = '40rem' }) {
  const tt = useT();
  const title = normalizeRichText(tt(slide.title) || '').replace(/&nbsp;| /g, ' ');
  const sub = normalizeRichText(tt(slide.subtitle) || '');
  const subText = sub.replace(/<[^>]*>/g, '').replace(/&nbsp;| /g, ' ').trim();
  const desc = tt(slide.description) ? normalizeRichText(tt(slide.description)) : '';
  return (
    <div className={`mh-enter flex flex-col gap-6 ${center ? 'items-center text-center' : ''}`} style={{ maxWidth: maxW }}>
      {subText && <div className={`mh-eyebrow mh-rich ${center ? 'mh-eyebrow--center' : ''}`} data-testid="hero-subtitle" dangerouslySetInnerHTML={{ __html: sub }} />}
      {title && <h1 className="mh-title mh-rich text-white font-bold" style={{ fontFamily: SERIF, fontSize: titleSize, lineHeight: 1.04, letterSpacing: '-.01em', textWrap: 'balance', color: '#ffffff' }} data-testid="hero-title" dangerouslySetInnerHTML={{ __html: title }} />}
      {desc && <div className="mh-rich text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,.8)', maxWidth: '36rem' }} data-testid="hero-description" dangerouslySetInnerHTML={{ __html: desc }} />}
      <div className="pt-2 w-full sm:w-auto"><Ctas slide={slide} center={center} /></div>
    </div>
  );
}

const Bg = ({ slide, style }) => {
  const bg = slide.background || slide.background_image || '';
  return bg ? <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bg})`, ...style }} /> : null;
};

// Same gutter as the full-width navbar (Navbar.js fullWidth) so the hero copy lines up with the logo.
const PAD = { paddingLeft: 'clamp(20px, 3.5vw, 64px)', paddingRight: 'clamp(20px, 3.5vw, 64px)' };

function Cinematic({ slide, slides, current, pick }) {
  const tt = useT();
  const [open, setOpen] = useState(false);
  const isVideo = slide.slide_type === 'video' && slide.video_embed;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const plain = (s) => normalizeRichText(tt(s.title) || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;| /g, ' ').replace(/\s+/g, ' ').trim();
  const thumb = slide.photo || slide.background || slide.background_image || '';
  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <Bg slide={slide} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, color-mix(in srgb, var(--color-primary,#000c52) 92%, #000) 0%, color-mix(in srgb, var(--color-primary,#000c52) 45%, transparent) 55%, color-mix(in srgb, var(--color-primary,#000c52) 20%, transparent) 100%)' }} />
      <div className="relative z-10 w-full pt-32 pb-10" style={PAD}>
        <div className="flex flex-col-reverse lg:flex-row lg:items-end gap-10 lg:gap-16">
          <div className="flex-1"><Copy slide={slide} titleSize="clamp(2.7rem, 6vw, 5.75rem)" maxW="46rem" /></div>
          {hasMedia(slide) && (
            isVideo ? (
              <button type="button" onClick={() => setOpen(true)} className="group self-start lg:self-end flex items-center gap-4 text-white text-left" aria-label="Play video" data-testid="hero-video-open">
                <span className="relative block w-44 md:w-56 aspect-[16/10] rounded overflow-hidden border-2" style={{ borderColor: 'rgba(255,255,255,.8)' }}>
                  {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="grid place-items-center w-12 h-12 rounded-full transition-transform duration-200 group-active:scale-95" style={{ background: 'var(--color-accent,#ff9c1b)', color: 'var(--accent-ink,#0f172a)' }}><Play className="w-5 h-5 ml-0.5" fill="currentColor" /></span>
                  </span>
                </span>
              </button>
            ) : (
              <div className="self-start lg:self-end w-56 md:w-72 aspect-[16/10] rounded overflow-hidden border-2 shadow-2xl" style={{ borderColor: 'rgba(255,255,255,.8)' }}><Media slide={slide} /></div>
            )
          )}
        </div>
        {slides.length > 1 && (
          <div className="mt-12 grid gap-4" style={{ gridTemplateColumns: `repeat(${slides.length}, minmax(0,1fr))` }}>
            {slides.map((s, i) => (
              <button key={i} type="button" onClick={() => pick(i)} className="text-left pt-3 pb-3 md:pb-0 text-xs md:text-sm transition-colors duration-200" style={{ borderTop: `2px solid ${i === current ? 'var(--color-accent,#ff9c1b)' : 'rgba(255,255,255,.25)'}`, color: i === current ? '#fff' : 'rgba(255,255,255,.6)' }} data-testid={`hero-dot-${i}`}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{String(i + 1).padStart(2, '0')}</span>
                <span className="hidden md:inline"> · {plain(s).slice(0, 48)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {open && isVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10" style={{ background: 'rgba(0,0,0,.86)' }} onClick={() => setOpen(false)} role="dialog" aria-modal="true">
          <button type="button" onClick={() => setOpen(false)} className="absolute top-5 right-5 text-white/80 hover:text-white" aria-label="Close"><X className="w-7 h-7" /></button>
          <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe src={`${embedUrl(slide.video_embed)}${embedUrl(slide.video_embed).includes('?') ? '&' : '?'}autoplay=1`} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen frameBorder="0" title="Hero video" />
          </div>
        </div>
      )}
    </section>
  );
}

export default function ModernHero({ slides, data }) {
  const allSlides = useMemo(() => (slides && slides.length > 0 ? slides : (data?.title ? [data] : [])), [slides, data]);
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (allSlides.length <= 1) return undefined;
    const delay = Number(allSlides[current]?.delay) || 9400;
    const timer = setTimeout(() => { setCurrent(p => (p + 1) % allSlides.length); setAnimKey(k => k + 1); }, delay);
    return () => clearTimeout(timer);
  }, [current, allSlides]);

  if (allSlides.length === 0) return null;
  const idx = current % allSlides.length;
  const pick = (i) => { setCurrent(i); setAnimKey(k => k + 1); };
  return (
    <>
      <style>{CSS}</style>
      <Cinematic key={animKey} slide={allSlides[idx]} slides={allSlides} current={idx} pick={pick} />
    </>
  );
}
